import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import {
  ColumnDef,
  PaginationState,
} from "@tanstack/react-table";
import {
  BookOpen,
  Search,
  TrendingUp,
  TrendingDown,
  Scale,
  X,
  Download,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DataTable from "@/components/datatable/Datatable";
import { Skeleton } from "@/components/ui/skeleton";

/* ──────────────── Types ──────────────── */

interface Account {
  id: number;
  code: string;
  name: string;
}

interface LedgerEntry {
  id: number;
  date: string;
  journalNumber: string;
  accountCode: string;
  accountName: string;
  narration: string;
  debit: number;
  credit: number;
  runningBalance: number;
}

interface LedgerResponse {
  results: LedgerEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalDebits: number;
    totalCredits: number;
    netBalance: number;
  };
}

/* ──────────────── Helpers ──────────────── */

const formatMoney = (v: number) => `${v?.toLocaleString()} FRW`;

/* ──────────────── Main Component ──────────────── */

export default function Ledger() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setAccountId("");
    setStartDate("");
    setEndDate("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const hasActiveFilters =
    debouncedSearch || accountId || startDate || endDate;

  /* ── Accounts dropdown data ── */
  const { data: accounts = [] } = useQuery<Account[]>(
    ["accounts-list"],
    async () => {
      const { data } = await api.get("/accounts");
      return data?.data || data?.results || [];
    },
    { staleTime: 60_000 }
  );

  /* ── Ledger data ── */
  const ledgerQuery = useQuery<LedgerResponse>(
    [
      "ledger",
      debouncedSearch,
      accountId,
      startDate,
      endDate,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(pagination.pageIndex + 1));
      params.set("page_size", String(pagination.pageSize));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (accountId) params.set("accountId", accountId);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const { data } = await api.get(`/journals/ledger?${params.toString()}`);
      return data?.data || data;
    },
    { keepPreviousData: true }
  );

  const entries: LedgerEntry[] = ledgerQuery.data?.results || [];
  const paginationInfo = ledgerQuery.data?.pagination || {
    page: 1,
    pageSize: 15,
    total: 0,
    totalPages: 1,
  };
  const summary = ledgerQuery.data?.summary || {
    totalDebits: 0,
    totalCredits: 0,
    netBalance: 0,
  };

  /* ── Table columns ── */
  const columns = useMemo<ColumnDef<LedgerEntry>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => (
          <span className="text-sm text-slate-700">
            {row.original.date
              ? new Date(row.original.date).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: "journalNumber",
        header: "Journal #",
        cell: ({ row }) => (
          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.journalNumber}
          </span>
        ),
        size: 130,
      },
      {
        accessorKey: "accountCode",
        header: "Account Code",
        cell: ({ row }) => (
          <span className="text-sm font-medium text-slate-700 font-mono">
            {row.original.accountCode}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: "accountName",
        header: "Account Name",
        cell: ({ row }) => (
          <span className="text-sm text-slate-700">
            {row.original.accountName}
          </span>
        ),
        size: 180,
      },
      {
        accessorKey: "narration",
        header: "Narration",
        cell: ({ row }) => (
          <span className="text-sm text-slate-600 max-w-[260px] truncate block">
            {row.original.narration || "—"}
          </span>
        ),
        size: 260,
      },
      {
        accessorKey: "debit",
        header: "Debit (FRW)",
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-emerald-700">
            {row.original.debit > 0 ? formatMoney(row.original.debit) : "—"}
          </span>
        ),
        size: 130,
      },
      {
        accessorKey: "credit",
        header: "Credit (FRW)",
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-red-600">
            {row.original.credit > 0 ? formatMoney(row.original.credit) : "—"}
          </span>
        ),
        size: 130,
      },
      {
        accessorKey: "runningBalance",
        header: "Running Balance",
        cell: ({ row }) => (
          <span
            className={cn(
              "text-sm font-bold",
              row.original.runningBalance >= 0
                ? "text-emerald-700"
                : "text-red-600"
            )}
          >
            {formatMoney(row.original.runningBalance)}
          </span>
        ),
        size: 140,
      },
    ],
    []
  );

  /* ── Export handler ── */
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (accountId) params.set("accountId", accountId);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      params.set("export", "true");
      const { data } = await api.get(`/journals/ledger?${params.toString()}`);
      const exportData = data?.data?.results || data?.data || [];

      if (exportData.length === 0) return;

      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(
        exportData.map((e: LedgerEntry) => ({
          Date: e.date,
          "Journal #": e.journalNumber,
          "Account Code": e.accountCode,
          "Account Name": e.accountName,
          Narration: e.narration,
          "Debit (FRW)": e.debit,
          "Credit (FRW)": e.credit,
          "Running Balance": e.runningBalance,
        }))
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "General Ledger");
      XLSX.writeFile(wb, `general_ledger_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      // silent fail
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <div className="p-2 bg-[#0A9F43]/10 rounded-xl">
                <BookOpen className="h-6 w-6 text-[#0A9F43]" />
              </div>
              General Ledger
            </h1>
            <p className="text-sm text-slate-500 mt-1 ml-[52px]">
              View all journal entries with running balances per account
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                ledgerQuery.refetch();
              }}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <RefreshCw
                className={cn(
                  "h-4 w-4 mr-1.5",
                  ledgerQuery.isFetching && "animate-spin"
                )}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            title="Total Debits"
            value={formatMoney(summary.totalDebits)}
            icon={<TrendingUp className="h-5 w-5" />}
            color="green"
            loading={ledgerQuery.isLoading}
          />
          <SummaryCard
            title="Total Credits"
            value={formatMoney(summary.totalCredits)}
            icon={<TrendingDown className="h-5 w-5" />}
            color="red"
            loading={ledgerQuery.isLoading}
          />
          <SummaryCard
            title="Net Balance"
            value={formatMoney(summary.netBalance)}
            icon={<Scale className="h-5 w-5" />}
            color={summary.netBalance >= 0 ? "green" : "red"}
            loading={ledgerQuery.isLoading}
          />
        </div>

        {/* Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-2 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            {/* Account Filter */}
            <div className="min-w-[200px] flex-1 max-w-[300px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                Account
              </label>
              <Select
                value={accountId}
                onValueChange={(val) => {
                  setAccountId(val === "all" ? "" : val);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {accounts.map((acct) => (
                    <SelectItem key={acct.id} value={String(acct.id)}>
                      {acct.code} — {acct.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="min-w-[155px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                From Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  className="h-9 bg-slate-50 border-slate-200 pl-9"
                />
              </div>
            </div>

            {/* End Date */}
            <div className="min-w-[155px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                To Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  className="h-9 bg-slate-50 border-slate-200 pl-9"
                />
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[200px] max-w-[320px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Journal #, narration, account…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    const t = setTimeout(() => {
                      setDebouncedSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }, 400);
                    return () => clearTimeout(t);
                  }}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                />
              </div>
            </div>

            {/* Reset */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={entries}
          isLoading={ledgerQuery.isLoading}
          isFetching={ledgerQuery.isFetching}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          pageCount={paginationInfo.totalPages}
          setPagination={setPagination}
          onSearch={(val) => {
            setDebouncedSearch(val);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          showSearch={false}
          emptyMessage="No ledger entries found"
          emptyDescription="Try adjusting your filters or date range"
          loadingRows={8}
        />

        {/* Page info */}
        <div className="mt-3 text-xs text-slate-500 text-right">
          Showing{" "}
          {paginationInfo.total === 0
            ? 0
            : pagination.pageIndex * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(
            (pagination.pageIndex + 1) * pagination.pageSize,
            paginationInfo.total
          )}{" "}
          of {paginationInfo.total.toLocaleString()} entries
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Summary Card ──────────────── */

function SummaryCard({
  title,
  value,
  icon,
  color,
  loading,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}) {
  const colorMap: Record<
    string,
    { bg: string; iconBg: string; iconText: string; valueText: string }
  > = {
    green: {
      bg: "bg-[#0A9F43]/5",
      iconBg: "bg-[#0A9F43]/10",
      iconText: "text-[#0A9F43]",
      valueText: "text-[#0A9F43]",
    },
    red: {
      bg: "bg-red-50",
      iconBg: "bg-red-100",
      iconText: "text-red-600",
      valueText: "text-red-600",
    },
    blue: {
      bg: "bg-blue-50",
      iconBg: "bg-blue-100",
      iconText: "text-blue-600",
      valueText: "text-blue-600",
    },
  };
  const c = colorMap[color] || colorMap.green;

  return (
    <Card className={`border border-slate-200 shadow-sm ${c.bg}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 mb-1 truncate">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-7 w-28" />
            ) : (
              <p className={`text-lg font-bold ${c.valueText} truncate`}>
                {value}
              </p>
            )}
          </div>
          <div
            className={`p-2 rounded-lg ${c.iconBg} flex-shrink-0`}
          >
            <span className={c.iconText}>{icon}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
